import { useState } from 'react'
import { numberedPositions, positionStem, resizePositions } from '@kronos/domain'
import type { PartsEntry } from '@kronos/domain'
import type { ComponentType } from '../../../data'
import { ItemThumb } from '../ItemArt'
import { Stepper } from './controls'

/**
 * One part on the list: its picture, how many, and — for a part with its own
 * serial — the name of each place one goes (§2.6: a position *is* a BOM line).
 *
 * The count of a serialized part is the number of positions, so the stepper
 * grows and shrinks the names (`resizePositions`); "Name them" renumbers the
 * whole row from a stem (`numberedPositions`). A counted part takes an amount
 * and no position, because nobody needs to know which of the fourteen screws.
 */
export function PartRow({
  entry,
  type,
  issues,
  taken,
  fixed,
  onChange,
  onRemove,
}: {
  entry: PartsEntry
  /** Undefined when the item master no longer has it — shown, never hidden. */
  type: ComponentType | undefined
  /** One per position, null where fine. */
  issues: readonly (string | null)[]
  /** Positions used by the other rows. */
  taken: readonly string[]
  /** Positions already saved on the revision — never renamed implicitly. */
  fixed: readonly string[]
  onChange: (next: PartsEntry) => void
  onRemove: () => void
}): JSX.Element {
  const name = type?.name ?? 'An item that no longer exists'
  const [stem, setStem] = useState(() => stemOf(entry, name))

  return (
    <li className="ie-part">
      <div className="ie-part__head">
        <ItemThumb imageId={type?.imageId ?? null} category={type?.category ?? ''} name={name} size={44} />
        <span className="ie-part__main">
          <span className={`ie-part__name${type ? '' : ' critical'}`}>{name}</span>
          <span className="ie-part__meta">
            {entry.kind === 'slots' ? 'Own serial · one position each' : `Counted · used up by the build`}
          </span>
        </span>
        <span className="ie-part__qty">
          {entry.kind === 'slots' ? (
            <Stepper
              label={`How many ${name}`}
              value={entry.positions.length}
              min={1}
              onChange={(n) => onChange({ ...entry, positions: resizePositions({ stem, current: entry.positions, count: n, taken, fixed }) })}
            />
          ) : (
            <>
              <Stepper label={`How many ${name}`} value={entry.quantity} min={0.001} decimal onChange={(quantity) => onChange({ ...entry, quantity })} />
              <span className="ie-part__unit">{type?.unit && type.unit !== 'each' ? type.unit : 'each'}</span>
            </>
          )}
        </span>
        <button type="button" className="danger ie-part__remove" onClick={onRemove} aria-label={`Remove ${name}`}>
          Remove
        </button>
      </div>

      {entry.kind === 'slots' ? (
        <div className="ie-part__slots">
          <ol className="ie-slots">
            {entry.positions.map((p, i) => (
              <li key={i} className={`ie-slot${issues[i] ? ' ie-slot--error' : ''}`}>
                <input
                  className="ie-slot__input"
                  value={p}
                  aria-label={`Position ${i + 1} for ${name}`}
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  onChange={(e) => onChange({ ...entry, positions: entry.positions.map((q, j) => (j === i ? e.target.value : q)) })}
                />
                {issues[i] ? <span className="ie-field__error">{issues[i]}</span> : null}
              </li>
            ))}
          </ol>
          {entry.positions.length > 0 ? (
            <div className="ie-rename">
              <span className="ie-rename__label">Name them</span>
              <input
                value={stem}
                onChange={(e) => setStem(e.target.value)}
                aria-label="Name stem"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
              />
              <button
                type="button"
                disabled={stem.trim() === ''}
                onClick={() => onChange({ ...entry, positions: numberedPositions(stem, entry.positions.length, taken) })}
              >
                {entry.positions.length === 1 ? `Call it ${positionStem(stem)}` : `${positionStem(stem)}_1 … _${entry.positions.length}`}
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </li>
  )
}

/** The stem a row's names already share — `joint` for joint_1…joint_7 — or the part's own. */
function stemOf(entry: PartsEntry, typeName: string): string {
  if (entry.kind !== 'slots' || entry.positions.length === 0) return positionStem(typeName)
  const first = entry.positions[0] ?? ''
  const m = /^(.*)_\d+$/.exec(first)
  return m?.[1] ?? first
}
